import { Trans } from "@lingui/macro";
import { UiBox, UiLink, UiSpinner } from "@smp/ui";
import clsx from "clsx";
import { FC, useCallback, useMemo } from "react";
import { InView } from "react-intersection-observer";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useQueryPolotnoStickers } from "@/queries/polotno";
import { customGeneratePath } from "@/utils";

import { useContextStickers } from "../../../../context/use_context_stickers";
import { NothingFound } from "./components/nothing_found";
import classes from "./content.module.css";

export const Content: FC = () => {
  const {
    pathPattern,
    state: { selectedTags, searchString, sortBy },
  } = useContextStickers();

  const {
    data: stickersData,
    isLoading: isStickersLoading,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useQueryPolotnoStickers({
    tags__slug__all: selectedTags,
    search: searchString || undefined,
    ordering: sortBy,
    page_size: 100,
  });

  const stickers = useMemo(
    () => stickersData?.pages.flatMap(({ results }) => results),
    [stickersData?.pages],
  );

  const handleChangeInViewSpinner = useCallback(
    (inView: boolean) => {
      if (inView && hasNextPage && fetchNextPage && !isFetching) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetching],
  );

  if (isStickersLoading) {
    return (
      <UiBox display="flex" justifyContent="center" height="100%">
        <UiSpinner title={<Trans>Loading</Trans>} />
      </UiBox>
    );
  }

  if (!stickers?.length) {
    return <NothingFound />;
  }

  return (
    <div className={classes.grid}>
      {stickers.map((sticker) => (
        <UiLink
          component={HelperAdapterLink}
          key={sticker.id}
          className={classes.grid__item}
          to={customGeneratePath(pathPattern, { stickerId: sticker.id })}
        >
          <img
            className={classes.grid__item__img}
            loading="lazy"
            alt={String(sticker.id)}
            src={sticker.image_preview_url}
          />
        </UiLink>
      ))}

      {hasNextPage && (
        <div className={clsx(classes.grid__item, classes.grid__item_loader)}>
          <InView onChange={handleChangeInViewSpinner}>
            <UiSpinner size={24} />
          </InView>
        </div>
      )}
    </div>
  );
};
